import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { updateAttendance } from '../../services/firebase/attendanceService';

const EditAttendanceModal = ({ show, record, onClose, onSuccess }) => {
  const { user } = useAuth();
  const canEditAttendance = ['admin', 'hr', 'manager'].includes((user?.role || '').toLowerCase());

  const [formData, setFormData] = useState({
    inTime: '',
    outTime: '',
    status: 'Present',
    workingHours: '',
    overtimeHours: '',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (record) {
      setFormData({
        inTime: record.inTime || '',
        outTime: record.outTime || '',
        status: record.status || 'Present',
        workingHours: record.workingHours || '',
        overtimeHours: record.overtimeHours || '',
        remarks: record.remarks || ''
      });
    }
  }, [record]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateHours = () => {
    if (formData.inTime && formData.outTime) {
      try {
        const [inHour, inMin] = formData.inTime.split(':').map(Number);
        const [outHour, outMin] = formData.outTime.split(':').map(Number);

        const inMinutes = inHour * 60 + inMin;
        const outMinutes = outHour * 60 + outMin;

        let diffMinutes = outMinutes - inMinutes;
        if (diffMinutes < 0) {
          diffMinutes += 24 * 60; // Handle overnight shift
        }

        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        const totalHours = (hours + minutes / 60).toFixed(2);

        setFormData(prev => ({
          ...prev,
          workingHours: totalHours,
          // Auto-update status to Present if it's currently Absent or an error status
          status: (prev.status === 'Absent' || prev.status.includes('ABSENT')) ? 'Present' : prev.status
        }));
      } catch (err) {
        console.error('Error calculating hours:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEditAttendance) {
      toast.error('Permission denied: Only Admin, HR, and Manager can edit attendance.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      // Include month and year from the record for Firebase collection lookup
      const updateData = {
        ...formData,
        month: record.month,
        year: record.year
      };

      const response = await updateAttendance(record.id, updateData);

      if (response.success || response.data?.success) {
        toast.success('Attendance updated successfully');
        onSuccess();
        onClose();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update attendance';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString();
    }
    if (date._seconds) {
      return new Date(date._seconds * 1000).toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Attendance Record</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {record && (
          <div className="mb-3">
            <strong>Employee:</strong> {record.employeeName || record.empName}<br />
            <strong>Date:</strong> {formatDate(record.date)}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>In Time</Form.Label>
                <Form.Control
                  type="time"
                  name="inTime"
                  value={formData.inTime}
                  onChange={handleChange}
                  onBlur={calculateHours}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Out Time</Form.Label>
                <Form.Control
                  type="time"
                  name="outTime"
                  value={formData.outTime}
                  onChange={handleChange}
                  onBlur={calculateHours}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                  <option value="Leave">Leave</option>
                  <option value="Half-day">Half-day</option>
                  <option value="WeeklyOff">Weekly Off</option>
                  <option value="Holiday">Holiday</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Working Hours</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="workingHours"
                  value={formData.workingHours}
                  onChange={handleChange}
                  placeholder="Auto-calculated"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Overtime Hours</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="overtimeHours"
                  value={formData.overtimeHours}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              placeholder="Add any notes or remarks..."
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditAttendanceModal;
